import { useState, useEffect, memo } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { openModal } from 'reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import { usePageTitle } from 'utils/pageTitle';

import DashboardLayout from 'pages/_rd_dashboard/DashboardLayout';
import { PropertyFavoriteIcon } from 'view/components/PropertyFavoriteIcon';

import {
	deletePortfolio,
	fetchPropertyInPortfolioSet,
} from 'data/reducers/portfoliosManagementReducer';

import {
	getPropertyNotifications,
	getPropertyStats,
	getPropertyPortfolios,
	getPropertyUsers,
} from 'data/reducers/propertyManagementReducer';

import RecentAlertsSection from './components/RecentAlertsSection';
import PropertyStatsCards from './components/PropertyStatsCards';
import AttachedPortfoliosSection from './components/AttachedPortfoliosSection';
import PropertyUsersSection from './components/PropertyUsersSection';
import QuicklinksSection from './components/QuicklinksSection';
import PropertyNotificationsSection from './components/PropertyNotificationsSection';

import './PortfolioPropertyPage.css';
import Card from 'components/Card';
import _ from 'lodash';

const PortfolioPropertyPage = memo(() => {
	const { portfolioId, propertyId } = useParams();
	const history = useHistory();
	const dispatch = useDispatch();

	// Local state for UI
	const [viewType, setViewType] = useState('grid'); // 'grid' or 'table'

	// Redux state - Portfolio data
	const {
		propertyInPortfolioSet: property,
		isLoadingPropertyInPortfolioSet: isLoadingProperty,
		propertyInPortfolioSetError,
	} = useSelector((state) => state.portfoliosManagement);

	// Redux state - Property-specific data
	const {
		notifications: propertyNotifications,
		isLoadingNotifications,
		notificationsError,
		notificationsTotalCount,
		notificationsCurrentPage,
		notificationsPerPage,
		stats: propertyStats,
		isFavorite: propertyIsFavorite,
		isTogglingFavorite,
		// New portfolio and users data from propertyManagement
		portfolios: propertyPortfolios,
		isLoadingPortfolios,
		portfoliosError,
		users: propertyUsers,
		isLoadingUsers,
		usersError,
	} = useSelector((state) => state.propertyManagement);

	// Get current user for potential future use
	// const { user: currentUser } = useSelector((state) => state.auth);

	// Property data with fallbacks
	const propertyData = property || {
		address: 'Loading...',
		block: 'N/A',
		lot: 'N/A',
		bin: 'N/A',
		isFavorite: false,
		openCertificates: 0,
		openViolations: 0,
		openInfrastructure: 0,
	};

	// Property stats (prioritize property management stats, fallback to property data)
	const stats = {
		openCertificates:
			propertyStats.openCertificates || propertyData.openCertificates || 0,
		openViolations:
			propertyStats.openViolations || propertyData.openViolations || 0,
		openInfrastructure:
			propertyStats.openInfrastructure || propertyData.openInfrastructure || 0,
	};

	// Attached portfolios data
	const displayPortfolios = propertyPortfolios || [];
	const displayUsers = propertyUsers || [];
	const displayNotifications = propertyNotifications || [];

	const portfolioProperties = _.flatMap(displayPortfolios, 'properties') || [];

	const notificationsPagination = {
		total: notificationsTotalCount || 0,
		currentPage: notificationsCurrentPage || 1,
		perPage: notificationsPerPage || 10,
	};

	// Set page title
	usePageTitle(`${propertyData.address} - Property Details`);

	const portfolioSet = _.first(propertyData?.portfolio_sets || []);

	// Fetch property data on component mount
	useEffect(() => {
		if (propertyId && portfolioId) {
			// Fetch property details within the portfolio set
			dispatch(fetchPropertyInPortfolioSet(portfolioId, propertyId))
				.then(() => {
					// Fetch property-specific data using new endpoints
					dispatch(getPropertyNotifications(propertyId, 1, 10));
					dispatch(getPropertyStats(propertyId));
					dispatch(getPropertyPortfolios(propertyId));
					dispatch(getPropertyUsers(portfolioId, propertyId));
				})
				.catch((error) => {
					console.error('Error fetching property in portfolio set:', error);
				});
		}
	}, [dispatch, propertyId, portfolioId]);

	// const handleDeleteProperty = () => {
	// 	// Open delete confirmation modal
	// 	dispatch(
	// 		openModal(MODAL_TYPES.CONFIRM, {
	// 			color: 'danger',
	// 			title: 'Remove Property',
	// 			onConfirm: () => {
	// 				// Handle property deletion and redirect back to portfolio
	// 				history.push(`/dashboard/portfolio/${portfolioId}`);
	// 			},
	// 			children: (
	// 				<div>
	// 					Are you sure you want to remove "{propertyData.address}" from this
	// 					portfolio? This action cannot be undone.
	// 				</div>
	// 			),
	// 			confirmLabel: 'Remove Property',
	// 		})
	// 	);
	// };

	const handlePortfolioView = (portfolioSetId) => {
		history.push(`/dashboard/portfolio/${portfolioSetId}`);
	};

	const handleEditPortfolio = (portfolioId) => {
		dispatch(openModal(MODAL_TYPES.EDIT_PORTFOLIO_MODAL, { portfolioId }));
	};

	const handleDeletePortfolio = (portfolioId) => {
		dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				color: 'danger',
				title: 'Delete Portfolio',
				onConfirm: async () => {
					try {
						await dispatch(deletePortfolio(portfolioId));
					} catch (error) {
						console.error('Error deleting portfolio:', error);
					}
				},
				children: (
					<div>
						Are you sure you want to delete this portfolio? This action cannot
						be undone.
					</div>
				),
				confirmLabel: 'Delete',
			}),
		);
	};

	const handleUserClick = (user) => {
		dispatch(
			openModal(MODAL_TYPES.PORTFOLIO_USER_VIEW_MODAL, {
				user,
				portfolioSetId: portfolioId,
				users: displayUsers,
				properties: portfolioProperties,
				hasAdminAccess: false, // Determine based on portfolio permissions
			}),
		);
	};

	const handleNotificationPageChange = (page) => {
		dispatch(getPropertyNotifications(propertyId, page, notificationsPerPage));
	};

	const handleQuickLinkClick = (type, propId) => {
		// Navigate to appropriate section based on type
		switch (type) {
			case 'landmarks':
				window.open(
					`/property/${propId}/${propertyData.address}/landmarks`,
					'_blank',
				);
				break;
			case 'violations':
				window.open(
					`/property/${propId}/${propertyData.address}/violations`,
					'_blank',
				);
				break;
			case 'certificates':
				window.open(
					`/property/${propId}/${propertyData.address}/certificates`,
					'_blank',
				);
				break;
			case 'infrastructure':
				window.open(
					`/property/${propId}/${propertyData.address}/infrastructure`,
					'_blank',
				);
				break;
			case 'history':
				window.open(
					`/property/${propId}/${propertyData.address}/history`,
					'_blank',
				);
				break;
			case 'documents':
				window.open(
					`/property/${propId}/${propertyData.address}/documents`,
					'_blank',
				);
				break;
			default:
				window.open(
					`/property/${propId}/${propertyData.address}/overview`,
					'_blank',
				);
		}
	};

	// Loading state
	if (isLoadingProperty) {
		return (
			<DashboardLayout>
				<div className="dashboard-section">
					<div
						className="d-flex justify-content-center align-items-center"
						style={{ height: '50vh' }}
					>
						<div className="spinner-border" role="status">
							<span className="visually-hidden">Loading...</span>
						</div>
					</div>
				</div>
			</DashboardLayout>
		);
	}

	// Error state
	if (propertyInPortfolioSetError && !property) {
		return (
			<DashboardLayout>
				<div className="dashboard-section">
					<div className="container mt-4">
						<div className="alert alert-warning">
							Property not found or you don't have permission to view it.
						</div>
					</div>
				</div>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<div className="dashboard-section">
				<div className="dashboard-section-header">
					<h1>
						<i className="bi bi-building me-3" />
						Property Details
					</h1>
				</div>

				<div className="portfolio-detail-page portfolio-property-page v3-scope">
					{/* Property Header */}
					<div className="portfolio-header">
						<div className="header-content">
							<div className="portfolio-title-section">
								<div className="property-info">
									<h1 className="portfolio-name">
										{propertyData.address} [{propertyData.block}/
										{propertyData.lot}] [{propertyData.bin}]
									</h1>

									{/* Property Breadcrumb */}
									<nav
										className="property-breadcrumb mt-2"
										aria-label="Property breadcrumb"
									>
										<ol className="breadcrumb mb-0">
											<li className="breadcrumb-item">
												<button
													type="button"
													className="btn btn-link p-0 text-decoration-underline"
													onClick={() => history.push('/dashboard/portfolio')}
													title="Go to All Portfolios"
												>
													All Portfolios
												</button>
											</li>
											<li className="breadcrumb-item">
												<button
													type="button"
													className="btn btn-link p-0 text-decoration-underline"
													onClick={() =>
														history.push(`/dashboard/portfolio/${portfolioId}`)
													}
													title={`Go to ${portfolioSet?.name || 'Portfolio'}`}
												>
													{portfolioSet?.name || 'Portfolio'}
												</button>
											</li>
											<li
												className="breadcrumb-item active"
												aria-current="page"
											>
												{propertyData.address}
											</li>
										</ol>
									</nav>
								</div>
							</div>

							<div className="portfolio-actions">
								{/* Star (Favorite) Button */}
								<PropertyFavoriteIcon
									property={propertyData}
									isFavorite={
										propertyIsFavorite ||
										propertyData.isFavorite ||
										propertyData.is_favorite
									}
									isLoading={isTogglingFavorite}
									size="large"
									className="as-button me-2"
									showTooltip={true}
								/>

								{/* View Property Button */}

								<Link
									className="btn btn-primary"
									to={`/property/${propertyData.address}/overview`}
									title="View property details"
								>
									<i className="bi bi-eye" />
									View Property
								</Link>
							</div>
						</div>
					</div>

					{/* Property Stats Cards */}
					<Card title="" type="secondary" className="mb-4 mt-4">
						<PropertyStatsCards stats={stats} />
					</Card>

					{/* Attached Portfolios Section */}
					<Card
						title="Portfolios Associated"
						type="secondary"
						className="portfolios-card mb-4"
					>
						<AttachedPortfoliosSection
							portfolios={displayPortfolios}
							isLoading={isLoadingPortfolios}
							error={portfoliosError}
							viewType={viewType}
							setViewType={setViewType}
							onPortfolioView={handlePortfolioView}
							onPortfolioEdit={handleEditPortfolio}
							onPortfolioDelete={handleDeletePortfolio}
						/>
					</Card>

					{/* Two Column Section: Quicklinks and Notifications */}
					<div className="row mb-4">
						<div className="col-lg-6 col-md-12 mb-4 mb-lg-0">
							<Card
								title="Quicklinks"
								type="secondary"
								className="quicklinks-card h-100"
							>
								<QuicklinksSection
									propertyId={propertyId}
									propertyAddress={propertyData.address}
									propertyData={propertyData}
									onQuickLinkClick={handleQuickLinkClick}
								/>
							</Card>
						</div>
						<div className="col-lg-6 col-md-12">
							<Card
								title="Notifications"
								type="secondary"
								className="notifications-card h-100"
							>
								<PropertyNotificationsSection
									propertyId={propertyId}
									propertyAddress={propertyData.address}
									portfolioSetId={portfolioId}
								/>
							</Card>
						</div>
					</div>

					{/* Property Users Section */}
					<Card title="Users" type="secondary" className="users-card mb-4">
						<PropertyUsersSection
							users={displayUsers}
							isLoading={isLoadingUsers}
							error={usersError}
							onUserClick={handleUserClick}
						/>
					</Card>

					{/* Recent Alerts Section */}
					<Card
						title="Recent Alerts"
						type="secondary"
						className="alerts-card mb-4"
					>
						<RecentAlertsSection
							notifications={displayNotifications}
							isLoading={isLoadingNotifications}
							error={notificationsError}
							pagination={notificationsPagination}
							onPageChange={handleNotificationPageChange}
						/>
					</Card>
				</div>
			</div>
		</DashboardLayout>
	);
});

export default PortfolioPropertyPage;
